import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { outboundAllowlistApi, OutboundAllowlistState } from '../services/api';
import InfoTip from '../components/common/InfoTip';

export const formatUsPhone = (digits: string): string =>
  /^\d{10}$/.test(digits) ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}` : digits;

const errorText = (err: any, fallback: string): string =>
  err?.response?.data?.error || err?.message || fallback;

interface Props {
  state: OutboundAllowlistState;
  onChange: (next: OutboundAllowlistState) => void;
}

const ApprovedOutboundSmsTab: React.FC<Props> = ({ state, onChange }) => {
  const [number, setNumber] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const add = async () => {
    setError(null);
    setNotice(null);
    setBusy('add');
    try {
      const res = await outboundAllowlistApi.add(number, label);
      onChange(res.data);
      setNotice(`${number.trim()} can now receive texts from dev.`);
      setNumber('');
      setLabel('');
    } catch (err: any) {
      setError(errorText(err, 'Could not add the number.'));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (digits: string) => {
    if (!window.confirm(`Remove ${formatUsPhone(digits)}? Dev will stop texting this number immediately.`)) return;
    setError(null);
    setNotice(null);
    setBusy(digits);
    try {
      const res = await outboundAllowlistApi.remove(digits);
      onChange(res.data);
      setNotice(`${formatUsPhone(digits)} was removed.`);
    } catch (err: any) {
      setError(errorText(err, 'Could not remove the number.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        <InfoTip tip="The only phone numbers this dev server may text or fax. Every send is checked against this list and blocked unless every recipient is on it.">Approved Outbound SMS (dev only)</InfoTip>
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Dev sends texts and faxes only to the numbers below. Before every send it asks Spruce who the
        conversation goes to, and blocks the send unless every recipient is on this list. Changes apply
        to the next send, with no restart needed.
      </Typography>

      {state.loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {state.loadError}
        </Alert>
      )}
      {!state.enabled && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Outbound is switched off on dev (OUTBOUND_ENABLED is not true), so nothing is sent, even to these numbers.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start', mb: 2 }}>
        <TextField
          size="small"
          label={<InfoTip tip="A 10-digit US number in any format. A leading 1 is accepted.">Phone number</InfoTip>}
          placeholder="(305) 555-0123"
          value={number}
          onChange={(e) => setNumber(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && number.trim()) add(); }}
        />
        <TextField
          size="small"
          label={<InfoTip tip="A note saying whose number this is, up to 80 characters. For reference only.">Label (optional)</InfoTip>}
          placeholder="Whose number this is"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          inputProps={{ maxLength: 80 }}
        />
        <Button
          variant="contained"
          startIcon={busy === 'add' ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
          disabled={!number.trim() || busy !== null}
          onClick={add}
        >
          <InfoTip tip="Approves this number for texts and faxes from dev, starting with the next send. Admin only; your name is saved with it.">Add number</InfoTip>
        </Button>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><InfoTip tip="An approved phone number.">Number</InfoTip></TableCell>
              <TableCell><InfoTip tip="The note saved with the number.">Label</InfoTip></TableCell>
              <TableCell><InfoTip tip="When the number was approved.">Added</InfoTip></TableCell>
              <TableCell><InfoTip tip="The user who approved the number.">Added by</InfoTip></TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {state.numbers.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    No approved numbers. Dev will not text anyone.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {state.numbers.map((n) => (
              <TableRow key={n.number}>
                <TableCell>{formatUsPhone(n.number)}</TableCell>
                <TableCell>{n.label || '—'}</TableCell>
                <TableCell>{n.addedAt ? new Date(n.addedAt).toLocaleString() : '—'}</TableCell>
                <TableCell>{n.addedBy || '—'}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Remove">
                    <span>
                      <IconButton size="small" color="error" disabled={busy !== null} onClick={() => remove(n.number)}>
                        {busy === n.number ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <InfoTip tip="Removes this number after you confirm. Dev stops texting it from the next send." />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {state.updatedAt && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Last changed {new Date(state.updatedAt).toLocaleString()}{state.updatedBy ? ` by ${state.updatedBy}` : ''}
        </Typography>
      )}
    </Paper>
  );
};

export default ApprovedOutboundSmsTab;
